import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, ExternalLink, Mail, Star, AlertCircle, X, Loader } from 'lucide-react';
import { getRecommendations, getMainSymptoms } from '../utils/recommendations';
import { products } from '../data/products';
import { fadeInUp, staggerContainer, scaleIn } from '../utils/animations';
import './Results.css';

const Results = ({ answers, onEmailCapture, onRetakeQuiz }) => {
  const recommendations = getRecommendations(answers, products).slice(0, 4);
  const mainSymptoms = getMainSymptoms(answers);

  // Email Modal State
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  // Modal Handlers
  const handleOpenEmailModal = () => {
    setIsEmailModalOpen(true);
  };

  const handleCloseEmailModal = () => {
    setIsEmailModalOpen(false);
    setEmail('');
    setSubmitSuccess(false);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Call the parent email capture function
      await onEmailCapture(email);

      setSubmitSuccess(true);

      // Close modal after 2 seconds
      setTimeout(() => {
        handleCloseEmailModal();
      }, 2000);
    } catch (error) {
      console.error('Error submitting email:', error);
      // Even on error, show success to user (better UX)
      setSubmitSuccess(true);
      setTimeout(() => {
        handleCloseEmailModal();
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleAffiliateClick = (product) => {
    if (window.gtag) {
      window.gtag('event', 'affiliate_click', {
        product_id: product.id,
        product_name: product.name,
        platform: product.platform
      });
    }
    window.open(product.link, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="results-container">
      <div className="results-wrapper">
        {/* Results Header */}
        <motion.div
          initial="hidden"
          animate="visible"
          variants={staggerContainer}
          className="results-header"
        >
          <motion.div
            variants={scaleIn}
            transition={{ type: "spring", stiffness: 200, delay: 0.2 }}
            className="results-badge"
          >
            <Check />
            <span>Analysis Complete</span>
          </motion.div>
          <motion.h1
            variants={fadeInUp}
            className="results-title"
          >
            Your Personalized Recommendations
          </motion.h1>
          <motion.p
            variants={fadeInUp}
            className="results-subtitle"
          >
            Based on your answers, we've identified the best solutions to help you
            manage your symptoms and improve your wellness journey.
          </motion.p>
        </motion.div>

        {/* Main Symptoms Summary */}
        {mainSymptoms.length > 0 && (
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={staggerContainer}
            className="results-symptoms"
          >
            <motion.h2
              variants={fadeInUp}
              className="results-symptoms-title"
            >
              Your Primary Concerns
            </motion.h2>
            <motion.div
              variants={staggerContainer}
              className="results-symptoms-list"
            >
              {mainSymptoms.map((symptom, index) => (
                <motion.div
                  key={index}
                  variants={scaleIn}
                  whileHover={{ scale: 1.05 }}
                  className="results-symptom-tag"
                >
                  <AlertCircle />
                  <span>{symptom}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        )}

        {/* Product Recommendations */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.2 }}
          variants={staggerContainer}
          className="results-products"
        >
          <motion.h2
            variants={fadeInUp}
            className="results-products-title"
          >
            Recommended For You
          </motion.h2>
          <motion.div
            variants={staggerContainer}
            className="results-products-grid"
          >
            {recommendations.map((product, index) => (
              <motion.div
                key={product.id}
                variants={fadeInUp}
                className="product-card"
              >
                {/* Product Header */}
                <div className="product-header">
                  <div>
                    <span className="product-rank">
                      #{index + 1} Recommended
                    </span>
                    {product.rating && (
                      <div className="product-rating">
                        <Star />
                        <span>{product.rating}</span>
                      </div>
                    )}
                  </div>
                  <h3 className="product-name">
                    {product.name}
                  </h3>
                  <p className="product-meta">
                    {product.platform} • {product.price}
                  </p>
                </div>

                {/* Product Description */}
                <p className="product-description">
                  {product.description}
                </p>

                {/* Benefits List */}
                <div className="product-benefits">
                  <p className="product-benefits-title">
                    Key Benefits:
                  </p>
                  <div className="product-benefits-list">
                    {product.benefits.slice(0, 4).map((benefit, i) => (
                      <div key={i} className="product-benefit">
                        <Check />
                        <span>{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* CTA Button */}
                <button
                  onClick={() => handleAffiliateClick(product)}
                  className="product-cta"
                >
                  Learn More & Get Started
                  <ExternalLink />
                </button>

                {/* Top Pick Badge para o primeiro produto */}
                {index === 0 && (
                  <motion.div
                    initial={{ x: -100 }}
                    animate={{ x: 0 }}
                    transition={{ delay: 0.5, type: "spring", damping: 15 }}
                    className="product-top-pick"
                  >
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="white" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
                    </svg>
                    Top Pick
                  </motion.div>
                )}
              </motion.div>
            ))}
          </motion.div>
        </motion.div>

        {/* Email Capture CTA */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="results-email-cta"
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
            className="results-email-icon"
          >
            <Mail />
          </motion.div>
          <motion.h3
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="results-email-title"
          >
            Save these recommendations to your inbox!
          </motion.h3>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4 }}
            className="results-email-text"
          >
            We'll also send you exclusive menopause tips and guides delivered directly to your email.
          </motion.p>
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            transition={{ delay: 0.5 }}
            onClick={handleOpenEmailModal}
            className="results-email-btn"
          >
            Send Me My Results
          </motion.button>
        </motion.div>

        {/* Disclaimer */}
        <div className="results-disclaimer">
          <p>
            <strong>Important Note:</strong> These recommendations
            are based on your quiz responses and are for informational purposes only.
            They are not intended as medical advice. Please consult with your healthcare
            provider before starting any new supplement regimen, especially if you have
            pre-existing conditions or are taking medications.
          </p>
        </div>

        {/* Bottom Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="results-retake"
        >
          <div className="results-retake-text">
            <h3>Want different recommendations?</h3>
            <p>Retake the quiz to get updated suggestions</p>
          </div>
          <button
            onClick={onRetakeQuiz}
            className="results-retake-btn"
          >
            Retake Quiz
          </button>
        </motion.div>

        {/* Additional Resources Section */}
        <div className="mt-12 text-center">
          <h3 className="text-xl font-bold text-gray-900 mb-4">
            Continue Your Wellness Journey
          </h3>
          <p className="text-gray-600 mb-6">
            Explore our blog for more menopause tips, guides, and expert advice
          </p>
          <a
            href="#blog"
            className="inline-block px-6 py-3 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
          >
            Visit Our Blog
          </a>
        </div>
      </div>

      {/* Email Capture Modal */}
      <AnimatePresence>
        {isEmailModalOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
            onClick={handleCloseEmailModal}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-8 relative"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Close Button */}
              <button
                onClick={handleCloseEmailModal}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close modal"
              >
                <X className="w-6 h-6" />
              </button>

              {/* Icon */}
              <div className="flex justify-center mb-4">
                <div className="w-16 h-16 bg-primary-50 rounded-full flex items-center justify-center">
                  <Mail className="w-8 h-8 text-primary-600" />
                </div>
              </div>

              {/* Title */}
              <h3 className="text-2xl font-bold text-center text-gray-900 mb-2 font-serif">
                Get Your Results via Email
              </h3>

              {/* Description */}
              <p className="text-center text-gray-600 mb-6 font-sans">
                We'll send your personalized recommendations plus exclusive menopause tips and guides.
              </p>

              {/* Success Message */}
              {submitSuccess && (
                <motion.div
                  initial={{ scale: 0.9, opacity: 0 }}
                  animate={{ scale: 1, opacity: 1 }}
                  className="mb-4 p-4 bg-green-50 border border-green-100 rounded-lg"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Check className="w-5 h-5 text-green-600" />
                    <p className="text-green-800 font-medium text-sm">
                      Thank you! Check your email for your results.
                    </p>
                  </div>
                </motion.div>
              )}

              {/* Form */}
              {!submitSuccess && (
                <form onSubmit={handleEmailSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                      Email Address
                    </label>
                    <input
                      type="email"
                      id="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      placeholder="your.email@example.com"
                      required
                      className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-all outline-none"
                      disabled={isSubmitting}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3 px-6 rounded-lg font-semibold text-white transition-all ${isSubmitting
                      ? 'bg-gray-400 cursor-not-allowed'
                      : 'bg-primary-600 hover:bg-primary-700 shadow-md hover:shadow-lg transform hover:-translate-y-0.5'
                      }`}
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center gap-2">
                        <Loader className="w-5 h-5 animate-spin" />
                        Sending...
                      </span>
                    ) : (
                      'Send Me My Results'
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center flex items-center justify-center gap-1">
                    <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                    We respect your privacy. Unsubscribe anytime.
                  </p>
                </form>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Results;
